import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import type { TopUser } from '../../api/types';
import { displayUserId } from '../../lib/userId';

interface Props {
  users: TopUser[];
  loading?: boolean;
}

function formatTokens(value: number): string {
  if (value >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)}B`;
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
  return String(value);
}

export function TopUsersBarChart({ users, loading }: Props): JSX.Element {
  // Sort ascending so the largest bar lands at the top of a horizontal chart.
  const sorted = [...users].sort((a, b) => a.totalTokens - b.totalTokens);
  const labels = sorted.map((u) => displayUserId(u.userId));

  const option: EChartsOption = {
    grid: { left: 144, right: 80, top: 16, bottom: 56 },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params) => {
        const items = Array.isArray(params) ? params : [params];
        const first = items[0];
        if (!first) return '';
        const idx = first.dataIndex as number;
        const user = sorted[idx];
        if (!user) return '';
        return [
          `<strong>${displayUserId(user.userId)}</strong>`,
          `Tokens: ${user.totalTokens.toLocaleString('en-US')}`,
          `Sessions: ${user.sessionCount}`,
        ].join('<br/>');
      },
    },
    xAxis: {
      type: 'value',
      name: 'Tokens',
      nameLocation: 'middle',
      nameGap: 24,
      axisLabel: { formatter: (v: number) => formatTokens(v) },
    },
    yAxis: {
      type: 'category',
      data: labels,
      axisLabel: { fontFamily: 'ui-monospace, monospace', fontSize: 12 },
    },
    series: [
      {
        type: 'bar',
        barMaxWidth: 18,
        itemStyle: { color: '#6366f1', borderRadius: [0, 4, 4, 0] },
        data: sorted.map((u) => u.totalTokens),
        label: {
          show: true,
          position: 'right',
          formatter: (p) => formatTokens(p.value as number),
          fontSize: 11,
          color: 'inherit',
        },
      },
    ],
  };

  return (
    <ReactECharts
      option={option}
      style={{ height: Math.max(304, sorted.length * 32 + 88), width: '100%' }}
      showLoading={loading}
      notMerge
      lazyUpdate
    />
  );
}
